import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../../../components/ui/Dialog';

interface DeclineAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDecline: (matchId: number, reason?: string) => Promise<void>;
  matchId: number;
  matchDescription: string;
}

const DeclineAssignmentModal: React.FC<DeclineAssignmentModalProps> = ({
  isOpen,
  onClose,
  onDecline,
  matchId,
  matchDescription,
}) => {
  const { t } = useTranslation('tournament');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDecline = async () => {
    setIsSubmitting(true);
    try {
      await onDecline(matchId, reason.trim() || undefined);
      setReason('');
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setReason('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent size="sm" data-testid="decline-assignment-modal">
        <DialogHeader className="bg-red-50 dark:bg-red-900/20 border-b-red-200 dark:border-b-red-800">
          <DialogTitle className="flex items-center text-red-900 dark:text-red-300">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center me-3">
              <Icon name="X" size={20} className="text-red-600" />
            </div>
            <div>
              <div>{t('Decline Assignment')}</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <p className="text-foreground">
            {t('Are you sure you want to decline the assignment for')} <strong>{matchDescription}</strong>?
          </p>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              {t('Reason (optional)')}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('Enter reason for declining...')}
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors resize-none"
              disabled={isSubmitting}
              data-testid="decline-reason-textarea"
            />
            <p className="text-xs text-muted-foreground text-end">{reason.length}/500</p>
          </div>
        </DialogBody>

        <DialogFooter className="bg-muted">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            data-testid="decline-cancel-btn"
          >
            {t('Cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleDecline}
            disabled={isSubmitting}
            loading={isSubmitting}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="decline-confirm-btn"
          >
            {isSubmitting ? t('Declining...') : t('Decline')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeclineAssignmentModal;
